import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal, SectionHeading } from "../components/Reveal";
import { SUPPORT_OPTIONS } from "../constants/data";
import { ShieldCheck, CheckCircle2, Send, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-.\s\d]{7,20}$/;

const initialForm = {
  patient_name: "", patient_dob: "", patient_phone: "", patient_email: "", patient_address: "",
  emergency_name: "", emergency_relationship: "", emergency_phone: "", emergency_email: "",
  referrer_name: "", referrer_clinic: "", referrer_phone: "", referrer_email: "",
  reason: "", requested_support: [], other_support: "", care_considerations: "",
  preferred_start_date: "", preferred_days_hours: "",
  consent: false, signature_name: "", signature_date: "",
};

const Field = ({ label, name, required, error, children, hint }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-bold text-brand-navy">
      {label} {required && <span className="text-brand-teal" aria-hidden="true">*</span>}
    </label>
    <div className="mt-2">{children}</div>
    {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    {error && <p data-testid={`error-${name}`} className="mt-1.5 text-xs font-semibold text-red-700" role="alert">{error}</p>}
  </div>
);

const inputCls = (err) =>
  `w-full rounded-xl border ${err ? "border-red-400 bg-red-50/40" : "border-slate-300 bg-white"} px-4 py-3 text-base text-brand-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal transition-colors duration-300`;

const SectionCard = ({ num, title, children }) => (
  <Reveal>
    <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-10 shadow-[0_8px_30px_rgb(11,27,61,0.04)]">
      <div className="flex items-baseline gap-4">
        <span className="font-display italic text-3xl text-brand-teal/70">{num}</span>
        <h2 className="font-display text-xl md:text-2xl text-brand-navy">{title}</h2>
      </div>
      <div className="mt-7">{children}</div>
    </section>
  </Reveal>
);

export default function ReferralPage() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const toggleSupport = (option) => {
    setForm((f) => ({
      ...f,
      requested_support: f.requested_support.includes(option)
        ? f.requested_support.filter((o) => o !== option)
        : [...f.requested_support, option],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.patient_name.trim()) e.patient_name = "Full name is required.";
    if (!form.patient_dob) e.patient_dob = "Date of birth is required.";
    if (!form.patient_phone.trim()) e.patient_phone = "Phone number is required.";
    else if (!PHONE_RE.test(form.patient_phone.trim())) e.patient_phone = "Please enter a valid phone number.";
    if (!form.patient_email.trim()) e.patient_email = "Email address is required.";
    else if (!EMAIL_RE.test(form.patient_email.trim())) e.patient_email = "Please enter a valid email address.";
    if (!form.patient_address.trim()) e.patient_address = "Home address is required.";
    if (form.emergency_email && !EMAIL_RE.test(form.emergency_email.trim())) e.emergency_email = "Please enter a valid email address.";
    if (form.emergency_phone && !PHONE_RE.test(form.emergency_phone.trim())) e.emergency_phone = "Please enter a valid phone number.";
    if (form.referrer_email && !EMAIL_RE.test(form.referrer_email.trim())) e.referrer_email = "Please enter a valid email address.";
    if (form.referrer_phone && !PHONE_RE.test(form.referrer_phone.trim())) e.referrer_phone = "Please enter a valid phone number.";
    if (!form.reason.trim()) e.reason = "Please briefly describe the reason for referral.";
    if (form.requested_support.includes("Other") && !form.other_support.trim()) e.other_support = "Please describe the other support requested.";
    if (!form.consent) e.consent = "Consent is required before submitting the referral.";
    if (!form.signature_name.trim()) e.signature_name = "Please type the patient / authorized representative name.";
    if (!form.signature_date) e.signature_date = "Date is required.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Please complete the required fields highlighted below.");
      const first = document.querySelector('[role="alert"]');
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals`, form);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us at +1 (778) 837-2786.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="referral-page" className="bg-brand-paper min-h-screen">
      <Header />
      <main className="pt-[84px]">
        <div className="bg-brand-navy py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-4">
              <span className="h-px w-12 bg-brand-cyan" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-cyan">Secure Referral</span>
            </div>
            <h1 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight">Patient Referral Form</h1>
            <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-slate-300">
              Patients, family members, and healthcare professionals can use this form to refer someone for home care and support services from BMS Health Care Services Ltd.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              data-testid="referral-confirmation"
              className="rounded-2xl border border-brand-teal/30 bg-white p-10 md:p-14 text-center shadow-[0_16px_50px_rgb(0,128,128,0.10)]"
            >
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                <CheckCircle2 className="h-8 w-8" strokeWidth={1.5} />
              </span>
              <h2 className="mt-6 font-display text-3xl text-brand-navy">Thank You</h2>
              <p className="mt-4 max-w-xl mx-auto text-base md:text-lg leading-relaxed text-slate-600">
                Thank you. Your referral has been submitted successfully. A member of BMS Health Care Services will contact you soon.
              </p>
              <Link
                to="/"
                data-testid="confirmation-home-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-teal px-7 py-3.5 text-sm font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95"
              >
                Return to Home
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} noValidate data-testid="referral-form" className="space-y-8">
              <SectionCard num="1" title="Patient Information">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full Name" name="patient_name" required error={errors.patient_name}>
                    <input id="patient_name" data-testid="input-patient-name" type="text" autoComplete="name" value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} className={inputCls(errors.patient_name)} />
                  </Field>
                  <Field label="Date of Birth" name="patient_dob" required error={errors.patient_dob}>
                    <input id="patient_dob" data-testid="input-patient-dob" type="date" value={form.patient_dob} onChange={(e) => set("patient_dob", e.target.value)} className={inputCls(errors.patient_dob)} />
                  </Field>
                  <Field label="Phone Number" name="patient_phone" required error={errors.patient_phone}>
                    <input id="patient_phone" data-testid="input-patient-phone" type="tel" autoComplete="tel" value={form.patient_phone} onChange={(e) => set("patient_phone", e.target.value)} className={inputCls(errors.patient_phone)} />
                  </Field>
                  <Field label="Email Address" name="patient_email" required error={errors.patient_email}>
                    <input id="patient_email" data-testid="input-patient-email" type="email" autoComplete="email" value={form.patient_email} onChange={(e) => set("patient_email", e.target.value)} className={inputCls(errors.patient_email)} />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Home Address" name="patient_address" required error={errors.patient_address}>
                      <input id="patient_address" data-testid="input-patient-address" type="text" autoComplete="street-address" value={form.patient_address} onChange={(e) => set("patient_address", e.target.value)} className={inputCls(errors.patient_address)} />
                    </Field>
                  </div>
                </div>
              </SectionCard>

              <SectionCard num="2" title="Emergency / Family Contact">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Name" name="emergency_name" error={errors.emergency_name}>
                    <input id="emergency_name" data-testid="input-emergency-name" type="text" value={form.emergency_name} onChange={(e) => set("emergency_name", e.target.value)} className={inputCls(errors.emergency_name)} />
                  </Field>
                  <Field label="Relationship to Patient" name="emergency_relationship" error={errors.emergency_relationship}>
                    <input id="emergency_relationship" data-testid="input-emergency-relationship" type="text" value={form.emergency_relationship} onChange={(e) => set("emergency_relationship", e.target.value)} className={inputCls(errors.emergency_relationship)} />
                  </Field>
                  <Field label="Phone Number" name="emergency_phone" error={errors.emergency_phone}>
                    <input id="emergency_phone" data-testid="input-emergency-phone" type="tel" value={form.emergency_phone} onChange={(e) => set("emergency_phone", e.target.value)} className={inputCls(errors.emergency_phone)} />
                  </Field>
                  <Field label="Email" name="emergency_email" error={errors.emergency_email}>
                    <input id="emergency_email" data-testid="input-emergency-email" type="email" value={form.emergency_email} onChange={(e) => set("emergency_email", e.target.value)} className={inputCls(errors.emergency_email)} />
                  </Field>
                </div>
              </SectionCard>

              <SectionCard num="3" title="Referring Healthcare Professional / Clinic">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Physician / Healthcare Professional" name="referrer_name" error={errors.referrer_name}>
                    <input id="referrer_name" data-testid="input-referrer-name" type="text" value={form.referrer_name} onChange={(e) => set("referrer_name", e.target.value)} className={inputCls(errors.referrer_name)} />
                  </Field>
                  <Field label="Clinic / Organization" name="referrer_clinic" error={errors.referrer_clinic}>
                    <input id="referrer_clinic" data-testid="input-referrer-clinic" type="text" value={form.referrer_clinic} onChange={(e) => set("referrer_clinic", e.target.value)} className={inputCls(errors.referrer_clinic)} />
                  </Field>
                  <Field label="Phone Number" name="referrer_phone" error={errors.referrer_phone}>
                    <input id="referrer_phone" data-testid="input-referrer-phone" type="tel" value={form.referrer_phone} onChange={(e) => set("referrer_phone", e.target.value)} className={inputCls(errors.referrer_phone)} />
                  </Field>
                  <Field label="Email" name="referrer_email" error={errors.referrer_email}>
                    <input id="referrer_email" data-testid="input-referrer-email" type="email" value={form.referrer_email} onChange={(e) => set("referrer_email", e.target.value)} className={inputCls(errors.referrer_email)} />
                  </Field>
                </div>
              </SectionCard>

              <SectionCard num="4" title="Reason for Referral">
                <Field label="Please briefly describe why the patient may benefit from home care or additional support." name="reason" required error={errors.reason}>
                  <textarea id="reason" data-testid="input-reason" rows={5} value={form.reason} onChange={(e) => set("reason", e.target.value)} className={inputCls(errors.reason)} />
                </Field>
              </SectionCard>

              <SectionCard num="5" title="Requested Home Care Support">
                <p className="text-sm text-slate-600 mb-5">Please check all that apply.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {SUPPORT_OPTIONS.map((option) => (
                    <label
                      key={option}
                      data-testid={`support-option-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 cursor-pointer transition-colors duration-300 ${form.requested_support.includes(option) ? "border-brand-teal bg-brand-teal/5" : "border-slate-300 bg-white hover:border-brand-teal/50"}`}
                    >
                      <input
                        type="checkbox"
                        checked={form.requested_support.includes(option)}
                        onChange={() => toggleSupport(option)}
                        className="h-4 w-4 rounded border-slate-300 accent-[#008080]"
                      />
                      <span className="text-sm font-semibold text-brand-navy">{option}</span>
                    </label>
                  ))}
                </div>
                {form.requested_support.includes("Other") && (
                  <div className="mt-4">
                    <Field label="Please specify other support" name="other_support" required error={errors.other_support}>
                      <input id="other_support" data-testid="input-other-support" type="text" value={form.other_support} onChange={(e) => set("other_support", e.target.value)} className={inputCls(errors.other_support)} />
                    </Field>
                  </div>
                )}
              </SectionCard>

              <SectionCard num="6" title="Relevant Care Considerations">
                <Field label="Relevant information to assist us in understanding the patient's support needs." name="care_considerations" error={errors.care_considerations}>
                  <textarea id="care_considerations" data-testid="input-care-considerations" rows={4} value={form.care_considerations} onChange={(e) => set("care_considerations", e.target.value)} className={inputCls(errors.care_considerations)} />
                </Field>
              </SectionCard>

              <SectionCard num="7" title="Preferred Service Start Date">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Preferred Start Date" name="preferred_start_date" error={errors.preferred_start_date}>
                    <input id="preferred_start_date" data-testid="input-preferred-start-date" type="date" value={form.preferred_start_date} onChange={(e) => set("preferred_start_date", e.target.value)} className={inputCls(errors.preferred_start_date)} />
                  </Field>
                  <Field label="Preferred Days / Hours (if known)" name="preferred_days_hours" error={errors.preferred_days_hours}>
                    <input id="preferred_days_hours" data-testid="input-preferred-days-hours" type="text" value={form.preferred_days_hours} onChange={(e) => set("preferred_days_hours", e.target.value)} className={inputCls(errors.preferred_days_hours)} />
                  </Field>
                </div>
              </SectionCard>

              <SectionCard num="8" title="Consent to Share Information">
                <label
                  data-testid="consent-checkbox-label"
                  className={`flex items-start gap-4 rounded-xl border p-5 cursor-pointer transition-colors duration-300 ${errors.consent ? "border-red-400 bg-red-50/40" : form.consent ? "border-brand-teal bg-brand-teal/5" : "border-slate-300 bg-white hover:border-brand-teal/50"}`}
                >
                  <input
                    id="consent"
                    data-testid="input-consent"
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#008080]"
                  />
                  <span className="text-sm leading-relaxed text-slate-700">
                    I confirm that I am the patient or the patient's authorized representative and consent to the information contained in this referral form being shared with BMS Health Care Services Ltd. for the purpose of discussing, assessing, and arranging appropriate home care and support services. <span className="text-brand-teal font-bold" aria-hidden="true">*</span>
                  </span>
                </label>
                {errors.consent && <p data-testid="error-consent" className="mt-2 text-xs font-semibold text-red-700" role="alert">{errors.consent}</p>}
              </SectionCard>

              <SectionCard num="9" title="Signature">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Patient / Authorized Representative Name" name="signature_name" required error={errors.signature_name} hint="Typing your full legal name acts as your digital signature.">
                    <input id="signature_name" data-testid="input-signature-name" type="text" value={form.signature_name} onChange={(e) => set("signature_name", e.target.value)} className={`${inputCls(errors.signature_name)} signature-font text-lg`} />
                  </Field>
                  <Field label="Date" name="signature_date" required error={errors.signature_date}>
                    <input id="signature_date" data-testid="input-signature-date" type="date" value={form.signature_date} onChange={(e) => set("signature_date", e.target.value)} className={inputCls(errors.signature_date)} />
                  </Field>
                </div>
              </SectionCard>

              <Reveal>
                <div className="rounded-2xl border border-slate-200 bg-brand-mist/70 p-6 flex items-start gap-4">
                  <ShieldCheck className="h-6 w-6 shrink-0 text-brand-teal mt-0.5" strokeWidth={1.6} />
                  <p className="text-sm leading-relaxed text-slate-600">
                    <strong className="text-brand-navy">Privacy Notice:</strong> The information submitted through this form will be handled confidentially and used only for the purpose of discussing, assessing, and arranging appropriate home care and support services. BMS Health Care Services Ltd. respects your privacy and protects personal information in accordance with applicable privacy legislation.
                  </p>
                </div>
              </Reveal>

              <Reveal>
                <button
                  type="submit"
                  data-testid="submit-referral-button"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-brand-teal px-8 py-5 text-base font-bold text-white shadow-[0_12px_36px_rgba(0,128,128,0.35)] hover:bg-brand-navy transition-colors duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>Submitting securely…</>
                  ) : (
                    <>
                      <Lock className="h-5 w-5" strokeWidth={1.8} />
                      Submit Referral
                      <Send className="h-5 w-5" strokeWidth={1.8} />
                    </>
                  )}
                </button>
              </Reveal>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
